import { TextField, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton, Typography, useMediaQuery, useTheme, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LoadingButton } from "@mui/lab";
import ClearIcon from '@mui/icons-material/Clear';
import { SERVICEPOINT_SCHEMA } from "../ValidationSchemas"
import { cloneDeep, pick } from "lodash";
import { ConfirmDialog, MapLocation } from "modules/common";
import runJoiValidate from "utils/runJoiValidate";
import WorkHours from "./WorkHours";

export default function ServicePointDialog({
    openServicePointDialog,
    loading,
    isEditing,
    servicePoint,
    handleChange,
    onSetWholeWeek,
    onCreateServicePoint,
    onUpdateServicePoint,
    onDeleteServicePoint,
    onCloseDialog,
    error,
    isSuccess
}) {
    const { t } = useTranslation()
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [validationErrors, setValidationsErrors] = useState(null);


    const handleSubmit = () => {
        setValidationsErrors(null)
        if (isEditing) {
            let payload = pick(cloneDeep(servicePoint), ["name", "address", "workhours", "operationType"])
            const errors = runJoiValidate(SERVICEPOINT_SCHEMA, payload)
            if (errors) {
                setValidationsErrors(errors)
                return
            }
            Object.keys(payload.workhours)?.map(dayIndex => {
                if (payload.workhours[dayIndex].includes(null)) {
                    payload.workhours[dayIndex] = null
                } else {
                    payload.workhours[dayIndex][0] = payload.workhours[dayIndex][0].format("HH:mm:ss")
                    payload.workhours[dayIndex][1] = payload.workhours[dayIndex][1].format("HH:mm:ss")
                }
            })
            onUpdateServicePoint(payload)
        } else {
            let payload = cloneDeep(servicePoint)
            const errors = runJoiValidate(SERVICEPOINT_SCHEMA, payload)
            if (errors) {
                setValidationsErrors(errors)
                return
            }
            Object.keys(payload.workhours)?.map(dayIndex => {
                if (payload.workhours[dayIndex].includes(null) || !payload.workhours[dayIndex].length) {
                    payload.workhours[dayIndex] = null
                } else {
                    payload.workhours[dayIndex][0] = payload.workhours[dayIndex][0].format("HH:mm:ss")
                    payload.workhours[dayIndex][1] = payload.workhours[dayIndex][1].format("HH:mm:ss")
                }
            })
            onCreateServicePoint(payload)
        }
    }

    return (
        <Dialog
            maxWidth="md"
            dir={theme.direction}
            fullWidth
            fullScreen={fullScreen}
            open={openServicePointDialog}
            aria-labelledby="servicePoint-dialog-title"
        >
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                {`${isEditing ? t('Edit service point') : t('Add service point')}`}
                <IconButton onClick={onCloseDialog} color="primary">
                    <ClearIcon />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <Grid container spacing={5} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            name="name"
                            placeholder={t("Full Name")}
                            value={servicePoint?.name ?? ""}
                            onChange={handleChange}
                            helperText={validationErrors?.name ?? ''}
                            error={!!validationErrors?.name}
                            label={t("Full Name")} size="small" variant="outlined" margin="normal" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            name="address.area"
                            placeholder={t("Area")}
                            value={servicePoint?.address?.area ?? ""}
                            onChange={handleChange}
                            helperText={validationErrors?.address?.area ?? ''}
                            error={!!validationErrors?.address?.area}
                            label={t("Area")} size="small" variant="outlined" margin="normal" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            name="address.description"
                            placeholder={t("Description")}
                            value={servicePoint?.address?.description ?? ""}
                            onChange={handleChange}
                            helperText={validationErrors?.address?.description ?? ""}
                            error={!!validationErrors?.address?.description}
                            label={t("Description")} size="small" variant="outlined" margin="normal" />
                    </Grid>
                    {/*************** Worktime *****************/}
                    <Grid item xs={12} md={12}>
                        <Divider>
                            <Typography sx={{ fontWeight: "bold", my: 10 }} variant="h6">
                                {t("Worktime")}
                            </Typography>
                        </Divider>
                    </Grid>

                    <FormControl sx={{ mb: 5 }}>
                        <FormLabel id="operationType" sx={{ color: "black", fontWeight: "bold", mb: 10 }}>{t("Working hours system")}</FormLabel>
                        <RadioGroup
                            row
                            aria-labelledby="operationType"
                            name="operationType"
                            value={servicePoint?.operationType}
                            onChange={handleChange}
                        >
                            <FormControlLabel value="24/7" control={<Radio />} label={t("Work daily around the clock (24/7)")} />
                            <FormControlLabel value="FIXED_HOURS" control={<Radio />} label={t("Fixed hours")} />
                        </RadioGroup>
                    </FormControl>
                    {
                        servicePoint?.operationType === "FIXED_HOURS"
                        &&
                        <WorkHours onSetWholeWeek={onSetWholeWeek} workhours={servicePoint?.workhours} handleChange={handleChange} validationErrors={validationErrors} />
                    }
                    <Grid item xs={12} md={12}>
                        <Divider>
                            <Typography sx={{ fontWeight: "bold", my: 10 }} variant="h6">
                                {t("Location on map")}
                            </Typography>
                        </Divider>
                    </Grid>
                    <Grid item xs={12} md={12}>
                        {(!!validationErrors?.address?.coords?.latitude && !!validationErrors?.address?.coords?.longitude) &&
                            <Typography color="error">{t("Please enter the location on the map")}</Typography>
                        }
                        <MapLocation
                            coords={servicePoint?.address?.coords}
                            onChange={({ target }) => handleChange({ target: { ...target, name: `address.${target.name}` } })}
                            sx={{ height: '400px' }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <Divider />
            <DialogActions
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexDirection: 'row-reverse'
                }}
            >
                <Box>
                    <LoadingButton
                        size="small"
                        variant="contained"
                        disableElevation
                        onClick={handleSubmit}
                        loading={loading}
                        sx={{
                            textTransform: "none",
                            fontWeight: "bold",
                            mr: 5
                        }}
                    >
                        {isEditing ? t("Edit") : t("Add")}
                    </LoadingButton>
                    {isEditing &&
                        <LoadingButton
                            onClick={() => setOpenConfirmDialog(true)}
                            loading={loading}
                            color="error"
                            variant="contained"
                            sx={{ textTransform: "none" }}
                            size="small"
                        >
                            {t("Delete")}
                        </LoadingButton>
                    }
                </Box>
                <Box>
                    {isSuccess && <Typography color="success.main">{t("Done successfully")}</Typography>}
                    {error && <Typography color="error">{t(error)}</Typography>}
                </Box>
                <Button
                    variant="contained"
                    size="small"
                    sx={{
                        textTransform: "none",
                        fontWeight: "bold",
                    }}
                    onClick={onCloseDialog}>
                    {t("Close")}
                </Button>
            </DialogActions>
            <ConfirmDialog
                open={openConfirmDialog}
                onClose={() => setOpenConfirmDialog(false)}
                title={t("Warning")}
                content={t("Are you sure you want to delete it?")}
                confirmFn={() => {
                    onDeleteServicePoint(servicePoint.id)
                    setOpenConfirmDialog(false)
                }}
            />
        </Dialog>
    )
}